/**
 * Help without AI (TMMAPP-0142 U2 item 12, §I): free for everyone, even with AI off or an empty
 * wallet, a guest included.
 * - **Search** (`GET /v1/me/help/articles?q=`), the results with their excerpts;
 * - **browse by section** (`GET /v1/me/help/sections`, labelled and ordered by the app, then
 *   `GET …/articles?section=`);
 * - **an article** (`GET …/articles/{id}`) as safe Markdown, its pictures loaded through the API
 *   client when seen (`HelpImage`), a link to another article opening it here, and its related
 *   articles as links at the end. `404 HELP_ARTICLE_NOT_FOUND` reads "This article isn't available."
 *   with the way back.
 * The person's language is the server's choice, English filling a gap. An AI answer that cites an
 * article opens it here (`request`). Moving between views moves focus to the new view's heading.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { helpApi, type HelpArticle, type HelpArticleSummary, type HelpSection } from '@/api/help';
import { ErrorState } from '@/components/ErrorState';
import { PreviousIcon, SearchIcon } from '@/components/icons';
import { SafeMarkdown } from '@/components/SafeMarkdown';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { HelpImage } from './HelpImage';
import { orderedSections, sectionLabelKey } from './helpSections';

type View =
  | { kind: 'home' }
  | { kind: 'section'; key: string }
  | { kind: 'search'; q: string }
  | { kind: 'article'; id: string; back: Exclude<View, { kind: 'article' }> };

/** An article to open from outside (an AI answer's citation); `nonce` changes per request. */
export interface HelpRequest {
  articleId: string;
  nonce: number;
}

function ArticleList({
  articles,
  onOpen,
}: {
  articles: readonly HelpArticleSummary[];
  onOpen: (id: string) => void;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <List disablePadding sx={{ listStyle: 'none', m: 0 }} data-help-results="">
      {articles.map((article) => (
        <Box component="li" key={article.id} sx={{ borderBottom: `1px solid ${tokens.line}` }}>
          <ListItemButton
            onClick={() => onOpen(article.id)}
            data-help-article-row={article.id}
            sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1 }}
          >
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: tokens.text }}>
              {article.title}
            </Typography>
            {article.excerpt.length > 0 && (
              <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                {article.excerpt}
              </Typography>
            )}
          </ListItemButton>
        </Box>
      ))}
    </List>
  );
}

export function HelpBrowser({ request }: { request?: HelpRequest | null | undefined }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const headingId = useId();
  const [view, setView] = useState<View>({ kind: 'home' });
  const [query, setQuery] = useState('');
  const [sections, setSections] = useState<HelpSection[] | null>(null);
  const [sectionsError, setSectionsError] = useState<unknown>(null);
  const [articles, setArticles] = useState<HelpArticleSummary[] | null>(null);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [viewError, setViewError] = useState<unknown>(null);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const moved = useRef(false);

  // An article asked for from outside (an AI answer's citation) opens here.
  const [handled, setHandled] = useState<number | null>(null);
  if (request !== null && request !== undefined && request.nonce !== handled) {
    setHandled(request.nonce);
    setView((current) => ({
      kind: 'article',
      id: request.articleId,
      back: current.kind === 'article' ? current.back : current,
    }));
  }

  const go = useCallback((next: View) => {
    moved.current = true;
    setView(next);
  }, []);

  const loadSections = useCallback(async () => {
    try {
      setSections(await helpApi.sections());
      setSectionsError(null);
    } catch (error) {
      setSectionsError(error);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => void loadSections());
  }, [loadSections]);

  // Each view reads what it shows.
  useEffect(() => {
    let live = true;
    queueMicrotask(() => {
      if (!live) return;
      setViewError(null);
      setArticles(null);
      setArticle(null);
      const read =
        view.kind === 'section'
          ? helpApi.articles({ section: view.key }).then((list) => live && setArticles(list))
          : view.kind === 'search'
            ? helpApi.articles({ q: view.q }).then((list) => live && setArticles(list))
            : view.kind === 'article'
              ? helpApi.article(view.id).then((next) => live && setArticle(next))
              : Promise.resolve();
      read.catch((error: unknown) => {
        if (live) setViewError(error);
      });
    });
    return () => {
      live = false;
    };
  }, [view, reloadKey]);

  // A related article is named by its title (one read of the list, the first time one is needed).
  const needsTitles = article !== null && article.related.some((id) => titles[id] === undefined);
  useEffect(() => {
    if (!needsTitles) return undefined;
    let live = true;
    helpApi.articles().then(
      (all) => {
        if (live) setTitles(Object.fromEntries(all.map((item) => [item.id, item.title])));
      },
      () => {
        /* the ids stand in for the titles */
      },
    );
    return () => {
      live = false;
    };
  }, [needsTitles]);

  // Moving between views moves focus to the new view's heading.
  useEffect(() => {
    if (!moved.current) return;
    const node = document.getElementById(headingId);
    if (node !== null) {
      node.focus();
      moved.current = false;
    }
  });

  function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const q = query.trim();
    go(q.length === 0 ? { kind: 'home' } : { kind: 'search', q });
  }

  const openArticle = (id: string) =>
    go({ kind: 'article', id, back: view.kind === 'article' ? view.back : view });

  const heading = (text: string) => (
    <Typography
      component="h3"
      id={headingId}
      tabIndex={-1}
      sx={{ m: 0, fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}
    >
      {text}
    </Typography>
  );

  const backButton = (label: string, to: View) => (
    <Box>
      <Button size="small" startIcon={<PreviousIcon />} onClick={() => go(to)}>
        {label}
      </Button>
    </Box>
  );

  const loading = (
    <Typography sx={{ fontSize: 14, color: tokens.text2 }}>{t('common.loading')}</Typography>
  );

  let body;
  if (view.kind === 'article') {
    const back = view.back;
    const backLabel =
      back.kind === 'search'
        ? t('support.help.backToResults')
        : back.kind === 'section'
          ? t('support.help.backToSection', { section: t(sectionLabelKey(back.key)) })
          : t('support.help.backToSections');
    const missing = isApiError(viewError) && viewError.code === ErrorCodes.HELP_ARTICLE_NOT_FOUND;
    body = (
      <Stack sx={{ gap: 1.5 }} data-help-article={view.id}>
        {backButton(backLabel, back)}
        {article !== null ? (
          <>
            {heading(article.title)}
            {article.updated !== null && (
              <Typography sx={{ fontSize: 12, color: tokens.text2 }}>
                {t('support.help.updated', {
                  date: (() => {
                    try {
                      return formatCivilDate(article.updated, locale);
                    } catch {
                      return article.updated;
                    }
                  })(),
                })}
              </Typography>
            )}
            <SafeMarkdown
              source={article.body}
              renderImage={({ alt, src }) => <HelpImage alt={alt} src={src} />}
              onArticle={openArticle}
            />
            {article.related.length > 0 && (
              <Box component="nav" aria-label={t('support.help.related')}>
                <Typography
                  component="h4"
                  sx={{ m: 0, mb: 0.5, fontSize: 13, fontWeight: 600, color: tokens.text }}
                >
                  {t('support.help.related')}
                </Typography>
                <Box component="ul" sx={{ m: 0, paddingInlineStart: 2.5 }}>
                  {article.related.map((id) => (
                    <li key={id}>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => openArticle(id)}
                        data-related-article={id}
                        sx={{ fontSize: 14, color: tokens.link, textAlign: 'start' }}
                      >
                        {titles[id] ?? id}
                      </Link>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </>
        ) : viewError !== null ? (
          <>
            {heading(t('support.help.title'))}
            {missing ? (
              <Alert severity="info" data-article-missing="">
                {t('support.help.articleNotFound')}
              </Alert>
            ) : (
              <ErrorState
                variant="section"
                error={viewError}
                what={t('support.help.loadError')}
                onRetry={() => setReloadKey((key) => key + 1)}
              />
            )}
          </>
        ) : (
          loading
        )}
      </Stack>
    );
  } else if (view.kind === 'section' || view.kind === 'search') {
    body = (
      <Stack sx={{ gap: 1 }}>
        {backButton(t('support.help.backToSections'), { kind: 'home' })}
        {heading(
          view.kind === 'section'
            ? t(sectionLabelKey(view.key))
            : t('support.help.resultsHeading', { query: view.q }),
        )}
        {viewError !== null ? (
          <ErrorState
            variant="section"
            error={viewError}
            what={t('support.help.loadError')}
            onRetry={() => setReloadKey((key) => key + 1)}
          />
        ) : articles === null ? (
          loading
        ) : articles.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }} data-help-no-results="">
            {view.kind === 'search'
              ? t('support.help.noResults', { query: view.q })
              : t('support.help.emptySection')}
          </Typography>
        ) : (
          <ArticleList articles={articles} onOpen={openArticle} />
        )}
      </Stack>
    );
  } else {
    const shown = sections === null ? null : orderedSections(sections);
    body = (
      <Stack sx={{ gap: 1 }}>
        {heading(t('support.help.sectionsHeading'))}
        {sectionsError !== null && sections === null ? (
          <ErrorState
            variant="section"
            error={sectionsError}
            what={t('support.help.loadError')}
            onRetry={() => void loadSections()}
          />
        ) : shown === null ? (
          loading
        ) : shown.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: tokens.text2 }}>
            {t('support.help.empty')}
          </Typography>
        ) : (
          <List disablePadding sx={{ listStyle: 'none', m: 0 }} data-help-sections="">
            {shown.map((section) => (
              <Box
                component="li"
                key={section.key}
                sx={{ borderBottom: `1px solid ${tokens.line}` }}
              >
                <ListItemButton
                  onClick={() => go({ kind: 'section', key: section.key })}
                  data-help-section={section.key}
                  sx={{ justifyContent: 'space-between', gap: 1, py: 1 }}
                >
                  <Typography component="span" sx={{ fontSize: 14, color: tokens.text }}>
                    {t(sectionLabelKey(section.key))}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: 12, color: tokens.text2, flexShrink: 0 }}
                  >
                    {t('support.help.articleCount', { count: section.articleCount })}
                  </Typography>
                </ListItemButton>
              </Box>
            ))}
          </List>
        )}
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: 1.5 }} data-help-browser="">
      <Box component="form" role="search" onSubmit={search} sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          type="search"
          label={t('support.help.searchLabel')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
        />
        <Button
          type="submit"
          variant="outlined"
          aria-label={t('support.help.search')}
          sx={{ minWidth: 0, px: 1.25, flexShrink: 0 }}
        >
          <SearchIcon />
        </Button>
      </Box>
      {body}
    </Stack>
  );
}
